<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useOwnershipStore } from '@/stores/ownership'
import { chimeMuted, setChimeMuted, playChime } from '@/lib/chime'
import { useSessionStore } from '@/stores/session'
import type { OwnershipRequest } from '@/lib/types'

/**
 * "Qing wants your pursuit."
 *
 * Lives in the dashboard shell rather than on a page, because the request has
 * a ten-minute fuse and there is no page you can be relied on to be looking
 * at. It is a corner card rather than a modal on purpose: it must not block
 * whatever you were doing, since ignoring it is a legitimate answer with a
 * defined outcome.
 */
const ownership = useOwnershipStore()
const session = useSessionStore()

const muted = ref(chimeMuted())
function toggleMute() {
  muted.value = !muted.value
  setChimeMuted(muted.value)
  // Play on unmute so it's obvious what you just turned on.
  if (!muted.value) playChime()
}

// One timer for the whole prompt drives every countdown.
let ticker: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  ticker = setInterval(() => ownership.tick(), 1000)
})
onUnmounted(() => clearInterval(ticker))

const pending = computed(() => ownership.toDecide)
/** My own asks, so the person waiting can see the clock too. */
const waiting = computed(() => ownership.mineOpen)

function countdown(r: OwnershipRequest): string {
  const s = ownership.secondsLeft(r)
  if (s <= 0) return 'time up'
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Under a minute the bar turns red — this is the last chance to object. */
function urgent(r: OwnershipRequest): boolean {
  return ownership.secondsLeft(r) <= 60
}

function progress(r: OwnershipRequest): number {
  const total = Date.parse(r.decide_by) - Date.parse(r.created_at)
  if (!Number.isFinite(total) || total <= 0) return 0
  return Math.max(0, Math.min(100, (ownership.secondsLeft(r) * 1000 * 100) / total))
}

const busy = ref<string | null>(null)
async function decide(r: OwnershipRequest, action: 'approve' | 'deny' | 'withdraw') {
  busy.value = r.id
  try {
    await ownership.decide(r.id, action)
  } finally {
    busy.value = null
  }
}
</script>

<template>
  <div
    v-if="pending.length || waiting.length"
    class="fixed bottom-4 right-4 z-50 w-[min(24rem,calc(100vw-2rem))] flex flex-col gap-2"
  >
    <!-- Waiting on my answer. -->
    <div
      v-for="r in pending"
      :key="r.id"
      class="bg-surface-card border-2 rounded-lg shadow-lg overflow-hidden"
      :class="urgent(r) ? 'border-status-red' : 'border-status-amber'"
      role="alertdialog"
      :aria-label="`${r.requester_name} is asking for ${r.title}`"
    >
      <div class="px-3 py-2 flex items-center gap-2"
           :class="urgent(r) ? 'bg-status-red-soft' : 'bg-status-amber-soft'">
        <span class="text-label-caps uppercase"
              :class="urgent(r) ? 'text-status-red' : 'text-status-amber'">
          Hand-off request
        </span>
        <span class="ml-auto font-mono text-code-sm tabular-nums"
              :class="urgent(r) ? 'text-status-red font-bold' : 'text-status-amber'">
          {{ countdown(r) }}
        </span>
        <button
          class="text-ink-muted hover:text-ink"
          :aria-label="muted ? 'Unmute the alert sound' : 'Mute the alert sound'"
          :title="muted ? 'Alert sound off' : 'Alert sound on'"
          @click="toggleMute"
        >{{ muted ? '🔇' : '🔔' }}</button>
      </div>

      <!-- Time remaining as a bar: the number alone doesn't read at a glance. -->
      <div class="h-1 bg-surface-inset">
        <div
          class="h-full transition-all duration-1000 ease-linear"
          :class="urgent(r) ? 'bg-status-red' : 'bg-status-amber'"
          :style="{ width: progress(r) + '%' }"
        />
      </div>

      <div class="p-3">
        <p class="text-body-md text-ink leading-snug">
          <span class="font-semibold">{{ r.requester_name }}</span> is asking to take over
        </p>
        <RouterLink
          :to="`/o/${session.activeOrgId}/pursuits/${r.pursuit_id}`"
          class="block text-body-md font-semibold text-accent hover:underline line-clamp-2 mt-0.5"
        >{{ r.title }}</RouterLink>
        <p class="font-mono text-code-sm text-ink-muted mt-0.5">
          <span v-if="r.project_id" class="text-accent">{{ r.project_id }}</span>
          <span v-if="r.project_id && r.solicitation_number"> · </span>{{ r.solicitation_number }}
        </p>
        <p v-if="r.note" class="text-body-sm text-ink-soft italic mt-1.5 line-clamp-3">“{{ r.note }}”</p>

        <p class="text-body-sm text-ink-muted mt-2">
          It becomes theirs automatically if you don't answer.
        </p>

        <div class="mt-2.5 flex items-center gap-2">
          <button
            class="flex-1 px-3 py-2 rounded bg-navy hover:bg-navy-light text-white text-body-sm font-semibold disabled:opacity-50"
            :disabled="busy === r.id"
            @click="decide(r, 'approve')"
          >Hand it over</button>
          <button
            class="flex-1 px-3 py-2 rounded border border-subtle text-ink-soft hover:bg-surface-muted text-body-sm font-semibold disabled:opacity-50"
            :disabled="busy === r.id"
            @click="decide(r, 'deny')"
          >Keep it</button>
        </div>
      </div>
    </div>

    <!-- My own asks: quieter, and withdrawable. -->
    <div
      v-for="r in waiting"
      :key="r.id"
      class="bg-surface-card border border-subtle rounded-lg shadow-sm px-3 py-2.5"
    >
      <div class="flex items-center gap-2">
        <span class="text-label-caps uppercase text-ink-muted">Waiting on {{ r.owner_name || 'the owner' }}</span>
        <span class="ml-auto font-mono text-code-sm tabular-nums text-ink-muted">{{ countdown(r) }}</span>
      </div>
      <p class="text-body-sm text-ink line-clamp-2 mt-0.5">{{ r.title }}</p>
      <button
        class="mt-1.5 text-body-sm font-semibold text-ink-muted hover:text-ink underline disabled:opacity-50"
        :disabled="busy === r.id"
        @click="decide(r, 'withdraw')"
      >Withdraw</button>
    </div>
  </div>
</template>
